import { CircularProgress, FormControl, FormHelperText, InputAdornment, SelectProps, Tooltip } from '@mui/material';
import { castArray, first, get, isEmpty, map, orderBy, uniqBy } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import LazyLoadSelect from 'components/atoms/Dropdown/LazyLoadDropDown';
import { Job, JobStatus, JobType } from 'interfaces/job';
import { formatDateTimeForUser } from 'utils/dateTime';
import { humanize } from 'utils/helpers';
import { useJob } from 'utils/queryHooks/jobs/useJob';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';

export interface JobSelectProps extends Omit<SelectProps, 'onScroll' | 'label'> {
  debug?: boolean;
  label: string;
  jobType: JobType | JobType[];
  jobIds?: string[];
  jobName?: string;
  orchestrationIds?: string[];
  orchestrationIdTextFilter?: string;
  users?: Record<string, string>[];
  senders?: string[];
  slideIds?: string[];
  statuses?: JobStatus[];
  selectedJobId: string | null;
  setSelectedJobId: (jobId: string | null) => void;
  onLoadFullJob?: (job: Job) => void;
  autoAssignLastJob?: boolean;
  errors?: any;
  field?: string;
  helperText?: string;
  studyId?: string;
  withChildren?: boolean;
  formControlProps?: React.ComponentProps<typeof LazyLoadSelect>['formControlProps'];
}

const PAGE_SIZE = 6;

const JobSelect: React.FC<JobSelectProps> = ({
  debug = false,
  label,
  jobType,
  jobIds,
  jobName,
  orchestrationIds,
  orchestrationIdTextFilter,
  users,
  senders,
  slideIds,
  statuses = [JobStatus.Completed],
  withChildren = true,
  selectedJobId: selectedJobIdFromProps,
  autoAssignLastJob,
  setSelectedJobId,
  onLoadFullJob,
  errors,
  field,
  helperText,
  studyId,
  ...selectProps
}) => {
  const [jobsPage, setJobsPage] = useState(1);
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const setSelectedJobIdRef = useRef(setSelectedJobId);
  const onLoadFullJobRef = useRef(onLoadFullJob);

  useEffect(() => {
    setSelectedJobIdRef.current = setSelectedJobId;
  }, [setSelectedJobId]);

  useEffect(() => {
    onLoadFullJobRef.current = onLoadFullJob;
  }, [onLoadFullJob]);

  const jobsFilterKey = useMemo(
    () =>
      JSON.stringify({
        debug,
        jobType: castArray(jobType),
        jobIds,
        jobName,
        orchestrationIds,
        orchestrationIdTextFilter,
        users,
        senders,
        slideIds,
        statuses,
        withChildren,
        studyId,
      }),
    [
      debug,
      jobType,
      jobIds,
      jobName,
      orchestrationIds,
      orchestrationIdTextFilter,
      users,
      senders,
      slideIds,
      statuses,
      withChildren,
      studyId,
    ]
  );

  useEffect(() => {
    setJobsPage(1);
    setAllJobs((prevJobs) => {
      if (!selectedJobIdFromProps) {
        return [];
      }

      const selectedJob = prevJobs.find((job) => job.id === selectedJobIdFromProps);
      return selectedJob ? [selectedJob] : [];
    });
  }, [jobsFilterKey, selectedJobIdFromProps]);

  const {
    data: jobs,
    isFetching: isJobsFetching,
    isError: isJobsError,
  } = useJobs({
    debug,
    page: jobsPage,
    pageSize: PAGE_SIZE,
    additionalFilters: {
      ...(!isEmpty(jobType) && { types: castArray(jobType) }),
      jobIds,
      jobName,
      orchestrationIds,
      orchestrationIdTextFilter,
      users,
      senders,
      slideIds,
      statuses,
    },
    withChildren,
    ...(studyId ? { studyId } : {}),
  });

  const totalPages: number | null = jobs?.totalJobs ? Math.ceil(jobs.totalJobs / PAGE_SIZE) : null;

  const loadMoreJobs = () => {
    if (totalPages && jobsPage < totalPages) {
      setJobsPage((prevPage) => prevPage + 1);
    }
  };

  useEffect(() => {
    if (jobs) {
      setAllJobs((prevJobs) => orderBy(uniqBy([...prevJobs, ...(jobs.jobs as Job[])], 'id'), 'startedAt', 'desc'));
    }
  }, [jobs]);

  const selectedJobId = selectedJobIdFromProps
    ? selectedJobIdFromProps
    : // If autoAssignLastJob is true and no job is selected, select the most recent job
    autoAssignLastJob && !isEmpty(allJobs)
    ? first(allJobs)?.id || null
    : null;

  useEffect(() => {
    // Auto-assign the most recent job if none is selected and autoAssignLastJob is true
    if (autoAssignLastJob && !selectedJobIdFromProps && selectedJobId) {
      setSelectedJobIdRef.current(selectedJobId);
    }
  }, [autoAssignLastJob, selectedJobId, selectedJobIdFromProps]);

  const errorMessage = field ? get(errors, field)?.message : undefined;

  const { isLoading: isLoadingFullJob, data: fullJob } = useJob<Job>(selectedJobId || '', {
    enabled: !isEmpty(selectedJobId),
    retry: true,
  });

  const mergedJobs = useMemo(
    () =>
      orderBy(
        uniqBy(
          [...allJobs, ...(selectedJobIdFromProps && fullJob?.id === selectedJobIdFromProps ? [fullJob] : [])],
          'id'
        ),
        'startedAt',
        'desc'
      ),
    [allJobs, fullJob, selectedJobIdFromProps]
  );

  useEffect(() => {
    if (onLoadFullJobRef.current && !isLoadingFullJob && fullJob) {
      onLoadFullJobRef.current(fullJob);
    }
  }, [fullJob, isLoadingFullJob]);

  const jobOptions = useMemo(
    () =>
      map(
        mergedJobs.filter((job): job is Job & { id: string } => Boolean(job.id)),
        (job) => ({
          value: job.id,
          label: `${job.name} ${formatDateTimeForUser(job.startedAt)}`.trim(),
        })
      ),
    [mergedJobs]
  );

  return (
    <FormControl
      fullWidth
      variant="standard"
      error={Boolean(errorMessage)}
      className={errorMessage ? 'error' : undefined}
    >
      <LazyLoadSelect
        label={label}
        options={jobOptions}
        loadMoreOptions={loadMoreJobs}
        isLoading={isJobsFetching}
        required
        error={Boolean(errorMessage)}
        value={selectedJobId || ''}
        onChange={(event) => setSelectedJobId(event.target.value as string)}
        helperText={humanize(errorMessage) || helperText || (isJobsError ? 'Error loading jobs' : undefined)}
        loadingTitle="Loading jobs..."
        endAdornment={
          isLoadingFullJob ? (
            <InputAdornment position="end" sx={{ marginRight: 6 }}>
              <Tooltip title="Loading selected job details...">
                <CircularProgress size={20} />
              </Tooltip>
            </InputAdornment>
          ) : undefined
        }
        {...selectProps}
      />
      {Boolean(errorMessage) && (
        <FormHelperText>
          {`${errorMessage}`}.{' '}
          {selectedJobId
            ? isLoadingFullJob
              ? `Please wait, loading job details for job...`
              : `Problem with selected job (ID: ${selectedJobId})`
            : 'Please select a Threshold Job.'}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default JobSelect;
